import {OSkinableInit} from "back/commons/basis";
import {AppFrameDeskFeat, IAppBuilder, IAppCtx, JAppDef} from "back/core/appFrame";
import {Action, ActionHackCtx, ActionMenuDep, IAction} from "lib/commons/actions";
import {AreaAsync, IArea, SimpleTagArea} from "lib/commons/areas";
import {IReg, IRegPointer, REG} from 'lib/commons/registry';
import {JSX} from "lib/commons/xml/dom";
import "back/store/plugins/plugins_Perms";
import {IDepotEnv, IDepotUiEnv} from "lib/store/depot";
import {UrlTreeApp} from "back/store/apps/urlTreeApp";
import {DepotDeskApp, ODepotDeskAppInit} from "back/store/apps/depotDeskApp";
import {resPath} from "lib/store/res";
import {IO} from "lib/commons/io/io";
import {JLastDatas} from "lib/commons/lastDatas";
import {BrowseBoard, OBrowseBoardInit} from "back/store/views/browseBoard";

export interface JDepotPlgAppInit {
	/** NON IMPLEMENTE Vue ouverte par défaut */
	//openByDefault? : 'urlTree'|'desk',

	/** Vue desk */
	desk?: {
		/**
		 * Bouton affiché dans la barre du portail avec le titre spécifié, ou celui par défaut si "".
		 * Bouton masqué si null ou undefined (mais App bien déclarée)
		 */
		label?: string
	}

	/** Vue urlTree */
	urlTree?: {
		/**
		 * Bouton affiché dans la barre du portail avec le titre spécifié, ou celui par défaut si "".
		 * Bouton masqué si null ou undefined (mais App bien déclarée)
		 */
		label?: string
	}
}

/**
 * Accès à un dépot.
 * Peut être appelé n fois pour déclarer n dépots dans le front
 *
 * @param reg Registre à exploiter correspondant au depot, nécessaire en cas de front multi-universe.
 */
export function initApp(reg: IReg<IDepotEnv> = REG.reg, params: JDepotPlgAppInit) {

	const d = desk as AppFrameDeskFeat;

	const plgCode = reg.getPref<string>("plg.code", "xDepot");
	const plgName = reg.getPref<string>("plg.name") || "Dépôt";
	const plgUniverseCode = reg.env.universe.getId();

	// Création du menu 'Dépot'
	const actions: Action<IReg<IDepotEnv>>[] = [];
	if (params.desk) {
		d.addAppBuilder(new DepotDeskAppBuilder(reg));
		actions.push(new Action<IReg<IDepotEnv>>('desk')
			.setLabel(params.desk.label || "Gestion")
			.requireVisiblePerm("ui.depotPlg.desk")
			.setExecute((ctx: IReg<IDepotEnv>, ev: Event) => {
					d.findAndOpenApp({u: plgUniverseCode, dptPath: ""} as JDepotDeskAppDef, ev);
				}
			));

	}
	if (params.urlTree) {
		d.addAppBuilder(new UrlTreeAppBuilder(reg));
		if (params.urlTree.label != null) actions.push(new Action<IReg<IDepotEnv>>('urlTree')
			.setLabel(params.urlTree.label || "Consultation")
			.requireVisiblePerm("ui.depotPlg.urlTree")
			.setExecute((ctx: IReg<IDepotEnv>, ev: Event) => {
					d.findAndOpenApp({u: plgUniverseCode, utPath: ""} as JUrlTreeAppAppDef, ev);
				}
			));
	}

	/**
	 * Force le bon registre en ctx, indispensable pour la résolution des permissions.
	 */
	class WrapReg extends ActionHackCtx<Element, IReg<IDepotEnv>> {
		constructor(sub: IAction<IReg<IDepotEnv>>, public reg: IReg<IDepotEnv>) {
			super(sub);
		}

		wrapCtx(ctx: Element): IReg<IDepotEnv> {return this.reg}

		initButtonNode(buttonNode: Element, ctx: Element) {
			//Affectation du bon registre dans le contexte graphique.
			(buttonNode as IRegPointer<IDepotEnv>).reg = this.reg;
			this.sub.initButtonNode(buttonNode, this.reg);
		}
	}

	let main: IAction<IReg<IDepotEnv>>;
	if (actions.length > 1) {
		main = new ActionMenuDep().setLabel(plgName).setActions(actions.map((a) => new WrapReg(a, reg)));
	} else if (actions[0]) {
		main = actions[0].setLabel(plgName); //Force le label du dépot et nom le sous-label, "Gestion", "Consultation"...
	}

	if (main)
		REG.reg.addToList("appframe:header:toolbar", "depot_" + plgCode, 1, new WrapReg(main, reg));
}


/** Définition pour une UrlTreeApp */
export interface JUrlTreeAppAppDef extends JAppDef {
	/** Code de l'univers associé */
	u: string
	/** Path d'une res à afficher. */
	utPath: string
}

export class UrlTreeAppBuilder extends SimpleTagArea<IAppCtx<IDepotEnv>, UrlTreeApp> implements OSkinableInit, IAppBuilder<IDepotEnv, IDepotEnv> {

	skin?: string;

	skinOver?: string;

	constructor(public reg: IReg<IDepotEnv>) {
		super('store-urltree-app', reg.env.resolver.resolve(":back:store/apps/urlTreeApp.js"));
	}

	isAppDefMatch(data: JUrlTreeAppAppDef) {return typeof data.utPath === 'string' && data.u === this.reg.env.universe.getId()}

	newElt(ctx: IAppCtx<IDepotEnv> & OSkinableInit): UrlTreeApp {
		if (!ctx.skin) ctx.skin = this.skin;
		if (!ctx.skinOver) ctx.skinOver = this.skinOver;
		ctx.reg = this.reg;
		return (super.newElt(ctx) as UrlTreeApp).initialize(ctx);
	}
}

/** Définition pour une DepotDeskApp */
export interface JDepotDeskAppDef extends JAppDef {
	/** Code de l'univers associé */
	u: string

	/** Path d'une res à afficher. */
	dptPath: resPath
}

export class DepotDeskAppBuilder extends SimpleTagArea<IAppCtx<IDepotEnv>, DepotDeskApp> implements OSkinableInit, IAppBuilder<IDepotEnv, IDepotUiEnv> {

	skin?: string;

	skinOver?: string;

	constructor(public reg: IReg<IDepotEnv>) {
		super('store-depotdesk-app', reg.env.resolver.resolve(":back:store/apps/depotDeskApp.js"));
	}

	isAppDefMatch(data: JDepotDeskAppDef) {return 'dptPath' in data && data.u === this.reg.env.universe.getId()}

	newElt(ctx: IAppCtx<IDepotEnv> & OSkinableInit): DepotDeskApp {
		return (super.newElt(ctx) as DepotDeskApp).initialize({
			reg: this.reg,
			skin: this.skin,
			skinOver: this.skinOver,
			appDef: ctx.appDef,
			lastDatas: ctx.lastDatas,
			boards: this.reg.getList<IArea<IRegPointer<IDepotUiEnv>>>("depot:desk:boards") || [new BrowseBoardArea()] //[new BrowseBoardArea(), new BrowseBoardArea()]
		} as ODepotDeskAppInit);
	}
}

class BrowseBoardArea extends AreaAsync<IRegPointer<IDepotUiEnv>, BrowseBoard> {

	constructor() {
		super('browseBoard');
		this._label = "Arborescence";
		this._icon = "/@skin@/store/views/browseBoard/home.svg";
		this.requireLib(IO.asEndPoint("/@back@/store/views/browseBoard.js"));
	}

	buildBody(ctx: IRegPointer<IDepotUiEnv>, lastDatas?: JLastDatas): BrowseBoard {
		if (ctx.reg.isClosed) return null; //raceCond
		const init: OBrowseBoardInit = {
			reg: ctx.reg,
			lastDatas: lastDatas
		};
		return <store-browse-board î={init}/> as BrowseBoard;
	}
}
